/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: call.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  AckCallRequest,
  AckCallResponse,
  AdminSearchCallsRequest,
  AdminSearchCallsResponse,
  AdminUpdateCallRequest,
  AdminUpdateCallResponse,
  CancelCallRequest,
  CancelCallResponse,
  ClaimCallRequest,
  ClaimCallResponse,
  CreateCallRequest,
  CreateCallResponse,
  CreateCallStackRequest,
  CreateCallStackResponse,
  ExtractOutputRequest,
  ExtractOutputResponse,
  GenerateAccessTokenRequest,
  GenerateAccessTokenResponse,
  GetCallRequest,
  GetCallResponse,
  GetCallStackRequest,
  GetCallStackResponse,
  GetCallsRequest,
  GetCallsResponse,
  GetCaseRecordCallsRequest,
  GetCaseRecordCallsResponse,
  GetMyUserCallsRequest,
  GetMyUserCallsResponse,
  GetPayerHoursRequest,
  GetPayerHoursResponse,
  ListMyOrganizationCallsRequest,
  ListMyOrganizationCallsResponse,
  ListStackableCasesRequest,
  ListStackableCasesResponse,
  UpdateCallRequest,
  UpdateCallResponse,
} from '../types/call';

export const createCallService = (transport: Transport) => ({
  async createCall(
    request: CreateCallRequest,
    options: RequestOptions = {},
  ): Promise<CreateCallResponse> {
    const { organization_id, program_id, caserecord_id, ...body } = request;
    const url = getApiPath(API_METHODS.CallService.CreateCall, {
      organization_id,
      program_id,
      caserecord_id,
    });
    return transport.post<CreateCallResponse>(url, body, options);
  },

  async createCallStack(
    request: CreateCallStackRequest,
    options: RequestOptions = {},
  ): Promise<CreateCallStackResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CallService.CreateCallStack, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<CreateCallStackResponse>(url, body, options);
  },

  async getCallStack(
    request: GetCallStackRequest,
    options: RequestOptions = {},
  ): Promise<GetCallStackResponse> {
    const { organization_id, program_id, call_id } = request;
    const url = getApiPath(API_METHODS.CallService.GetCallStack, {
      organization_id,
      program_id,
      call_id,
    });
    return transport.get<GetCallStackResponse>(url, options);
  },

  async listStackableCases(
    request: ListStackableCasesRequest,
    options: RequestOptions = {},
  ): Promise<ListStackableCasesResponse> {
    const { organization_id, program_id, case_record_id, call_id } = request;
    const url = getApiPath(API_METHODS.CallService.ListStackableCases, {
      organization_id,
      program_id,
      case_record_id,
      call_id,
    });
    return transport.get<ListStackableCasesResponse>(url, options);
  },

  async getCall(request: GetCallRequest, options: RequestOptions = {}): Promise<GetCallResponse> {
    const { organization_id, program_id, caserecord_id, call_id } = request;
    const url = getApiPath(API_METHODS.CallService.GetCall, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.get<GetCallResponse>(url, options);
  },

  async getPayerHours(
    request: GetPayerHoursRequest,
    options: RequestOptions = {},
  ): Promise<GetPayerHoursResponse> {
    const { organization_id, program_id, payer_slug, ...params } = request;
    const url = getApiPath(API_METHODS.CallService.GetPayerHours, {
      organization_id,
      program_id,
      payer_slug,
    });
    return transport.get<GetPayerHoursResponse>(url, { params, ...options });
  },

  async updateCall(
    request: UpdateCallRequest,
    options: RequestOptions = {},
  ): Promise<UpdateCallResponse> {
    const { organization_id, program_id, caserecord_id, call_id, ...body } = request;
    const url = getApiPath(API_METHODS.CallService.UpdateCall, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.post<UpdateCallResponse>(url, body, options);
  },

  async getCaseRecordCalls(
    request: GetCaseRecordCallsRequest,
    options: RequestOptions = {},
  ): Promise<GetCaseRecordCallsResponse> {
    const { organization_id, program_id, caserecord_id, ...params } = request;
    const url = getApiPath(API_METHODS.CallService.GetCaseRecordCalls, {
      organization_id,
      program_id,
      caserecord_id,
    });
    return transport.get<GetCaseRecordCallsResponse>(url, { params, ...options });
  },

  async generateAccessToken(
    request: GenerateAccessTokenRequest,
    options: RequestOptions = {},
  ): Promise<GenerateAccessTokenResponse> {
    const { organization_id, program_id, caserecord_id, call_id, ...params } = request;
    const url = getApiPath(API_METHODS.CallService.GenerateAccessToken, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.get<GenerateAccessTokenResponse>(url, { params, ...options });
  },

  async getMyUserCalls(
    request: GetMyUserCallsRequest,
    options: RequestOptions = {},
  ): Promise<GetMyUserCallsResponse> {
    const url = getApiPath(API_METHODS.CallService.GetMyUserCalls);
    return transport.get<GetMyUserCallsResponse>(url, { params: request, ...options });
  },

  async listMyOrganizationCalls(
    request: ListMyOrganizationCallsRequest,
    options: RequestOptions = {},
  ): Promise<ListMyOrganizationCallsResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.CallService.ListMyOrganizationCalls, { organization_id });
    return transport.get<ListMyOrganizationCallsResponse>(url, { params, ...options });
  },

  async getCalls(
    request: GetCallsRequest,
    options: RequestOptions = {},
  ): Promise<GetCallsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CallService.GetCalls, { organization_id, program_id });
    return transport.get<GetCallsResponse>(url, { params, ...options });
  },

  async extractOutput(
    request: ExtractOutputRequest,
    options: RequestOptions = {},
  ): Promise<ExtractOutputResponse> {
    const { organization_id, program_id, caserecord_id, call_id } = request;
    const url = getApiPath(API_METHODS.CallService.ExtractOutput, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.post<ExtractOutputResponse>(url, {}, options);
  },

  async claimCall(
    request: ClaimCallRequest,
    options: RequestOptions = {},
  ): Promise<ClaimCallResponse> {
    const { organization_id, program_id, caserecord_id, call_id, ...body } = request;
    const url = getApiPath(API_METHODS.CallService.ClaimCall, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.post<ClaimCallResponse>(url, body, options);
  },

  async ackCall(request: AckCallRequest, options: RequestOptions = {}): Promise<AckCallResponse> {
    const { organization_id, program_id, caserecord_id, call_id, ...body } = request;
    const url = getApiPath(API_METHODS.CallService.AckCall, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.post<AckCallResponse>(url, body, options);
  },

  async cancelCall(
    request: CancelCallRequest,
    options: RequestOptions = {},
  ): Promise<CancelCallResponse> {
    const { organization_id, program_id, caserecord_id, call_id } = request;
    const url = getApiPath(API_METHODS.CallService.CancelCall, {
      organization_id,
      program_id,
      caserecord_id,
      call_id,
    });
    return transport.post<CancelCallResponse>(url, {}, options);
  },

  async adminSearchCalls(
    request: AdminSearchCallsRequest,
    options: RequestOptions = {},
  ): Promise<AdminSearchCallsResponse> {
    const url = getApiPath(API_METHODS.AdminCallService.AdminSearchCalls);
    return transport.get<AdminSearchCallsResponse>(url, { params: request, ...options });
  },

  async adminUpdateCall(
    request: AdminUpdateCallRequest,
    options: RequestOptions = {},
  ): Promise<AdminUpdateCallResponse> {
    const { call_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminCallService.AdminUpdateCall, { call_id });
    return transport.post<AdminUpdateCallResponse>(url, body, options);
  },
});

export type CallService = ReturnType<typeof createCallService>;
